import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { SessionProvider } from './store/session';
import { MotionProvider } from './motion';
import { isPackagedClient } from './api/client';
import { App } from './App';
import './styles/index.css';

/**
 * The packaged widget cannot use the History API, so it routes on the hash.
 *
 * A Tizen widget is loaded from `file://`, which makes its origin `null` — the app's own
 * `config.xml` records exactly that (`DETECTED ORIGIN: file://`). Blink refuses
 * `pushState`/`replaceState` to an absolute path from a null-origin document:
 *
 *   SecurityError: A history state object with URL 'file:///profiles' cannot be created
 *   in a document with origin 'null'
 *
 * React Router catches that and falls back to `window.location.assign('/profiles')`,
 * which on `file://` resolves to a path that does not exist. So every in-app navigation
 * would dead-end.
 *
 * The failure hides well, which is why it survived: the login screen is reached by
 * `<Route path="*">` and *renders* without navigating, so the widget installs, launches
 * and looks correct. The first `<Navigate>` happens after sign-in, on the way to the
 * profile picker. The Tizen smoke test could not have caught it either — it is one HTML
 * file with no framework, and proves the certificate chain, HTTPS, the remote keys and
 * one stream, not routing.
 *
 * `HashRouter` needs no History API and works from any origin. The web build keeps
 * `BrowserRouter`: real URLs are worth having where they are possible, and the nginx
 * config already serves the SPA shell for unknown paths.
 */
const Router = isPackagedClient ? HashRouter : BrowserRouter;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      MotionProvider sits inside the router and outside the app.

      Inside, because it reacts to the route: the player gets no smooth scrolling and no
      animation runtime, and every navigation makes Lenis and ScrollTrigger re-measure a
      document whose height just changed.

      Outside the app, because Lenis owns the page scroll and there can only be one owner.
      Starting it per-screen would mean two instances writing the same scroll position
      during a transition.

      It imports no animation library itself — GSAP and Lenis are fetched from inside
      effects, and only on a device that will use them.
    */}
    <Router>
      <SessionProvider>
        <MotionProvider>
          <App />
        </MotionProvider>
      </SessionProvider>
    </Router>
  </StrictMode>,
);

/*
 * The service worker caches the app shell only — never media. Registered after load so
 * it never competes with the first paint.
 *
 * Not in the packaged widget: service workers require a secure origin, and a `file://`
 * document has none, so the registration rejects. It was registered there anyway — with
 * `void`, which turns the rejection into an unhandled one rather than an error anybody
 * would see. The widget also has nothing to gain from it; it ships its own assets.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD && !isPackagedClient) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err: unknown) => {
      // A failed registration costs the offline shell and nothing else, so it is worth
      // one line in the console rather than a rejection nobody is listening for.
      console.warn('service worker registration failed', err);
    });
  });
}
